// Creator profile — 18-section chaptered form. PivotPhase-3.
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/brand/AppShell";
import { apiClient, useAuth } from "../auth/AuthContext";
import UploadsPanel from "../components/profile/UploadsPanel";

function CharCount({ value, max }) {
  if (!max) return null;
  const len = (value || "").length;
  const over = len > max;
  return (
    <p
      data-testid="field-char-counter"
      className={`mt-1 text-right font-mono text-[10px] uppercase tracking-[0.24em] ${over ? "text-brand-danger" : "text-brand-text/40"}`}
    >
      {len} / {max}
    </p>
  );
}

function Field({ field, value, onChange }) {
  const id = `f-${field.key}`;
  // 44px min-height on interactive controls — WCAG / Apple accessibility target for mobile.
  const common = "w-full min-h-[44px] rounded border border-brand-text/15 bg-[#141414] px-3 py-2.5 text-sm text-brand-text placeholder:text-brand-text/30 focus:border-brand-gold focus:outline-none";
  const label = (
    <label htmlFor={id} className="mb-1 block text-[10px] uppercase tracking-[0.24em] text-brand-text/60">
      {field.label}{field.required ? <span className="ml-1 text-brand-gold" aria-hidden="true">*</span> : ""}
    </label>
  );
  const helper = field.helper ? (
    <p data-testid={`field-${field.key}-helper`} className="mb-2 text-[11px] leading-relaxed text-brand-text/55">
      {field.helper}
    </p>
  ) : null;
  if (field.type === "longtext") {
    return (
      <div>{label}{helper}
        <textarea
          data-testid={`field-${field.key}`} id={id} rows={4}
          value={value || ""}
          maxLength={field.max_length || undefined}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          className={common}
        />
        <CharCount value={value || ""} max={field.max_length} />
      </div>
    );
  }
  if (field.type === "choice") {
    // 2026-07-22 — When a schema field has `allow_other: True`, picking "Other"
    // reveals a free-text specifier. We encode the composite as "Other: <text>"
    // in the stored value so no new field key is added to the schema.
    const OTHER_PREFIX = "Other: ";
    const allowOther = !!field.allow_other && (field.options || []).includes("Other");
    const isOtherActive = allowOther && typeof value === "string" && value.startsWith(OTHER_PREFIX);
    const selectedOpt = isOtherActive ? "Other" : value;
    const otherText = isOtherActive ? value.slice(OTHER_PREFIX.length) : "";
    return (
      <div>{label}{helper}
        <div className="flex flex-wrap gap-2">
          {field.options.map((opt) => (
            <button key={opt} type="button" data-testid={`field-${field.key}-opt-${opt.replace(/[^a-z0-9]/gi, "_")}`}
              onClick={() => onChange(field.key, opt === "Other" && allowOther ? OTHER_PREFIX : opt)}
              className={`min-h-[44px] rounded border px-4 py-2 text-xs uppercase tracking-[0.18em] ${selectedOpt === opt ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-text/15 text-brand-text/70 hover:border-brand-gold/40"}`}>{opt}</button>
          ))}
        </div>
        {allowOther && selectedOpt === "Other" && (
          <input
            type="text"
            data-testid={`field-${field.key}-other-specify`}
            value={otherText}
            onChange={(e) => onChange(field.key, OTHER_PREFIX + e.target.value)}
            placeholder="Specify…"
            maxLength={120}
            className={common + " mt-2"}
          />
        )}
      </div>
    );
  }
  if (field.type === "multichoice") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(field.key, arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt]);
    return (
      <div>{label}{helper}
        <div className="flex flex-wrap gap-2">
          {field.options.map((opt) => (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              className={`min-h-[44px] rounded border px-4 py-2 text-xs uppercase tracking-[0.18em] ${arr.includes(opt) ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-text/15 text-brand-text/70 hover:border-brand-gold/40"}`}>{opt}</button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "yesnomaybe") {
    return (
      <div>{label}{helper}
        <div className="flex gap-2">
          {["Yes", "No", "Maybe"].map((opt) => (
            <button key={opt} type="button" onClick={() => onChange(field.key, opt)}
              className={`min-h-[44px] flex-1 rounded border px-3 py-2 text-xs uppercase tracking-[0.18em] ${value === opt ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-text/15 text-brand-text/70 hover:border-brand-gold/40"}`}>{opt}</button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "money" || field.type === "number") {
    return (<div>{label}{helper}<input id={id} type="number" inputMode="numeric" placeholder={field.placeholder || ""} value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value === "" ? null : Number(e.target.value))} className={common} /></div>);
  }
  if (field.type === "date") {
    return (<div>{label}{helper}<input id={id} type="date" value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className={common} /></div>);
  }
  if (field.type === "ack") {
    return (
      <div>
        {field.helper && (
          <p data-testid={`field-${field.key}-helper`} className="mb-2 text-[11px] leading-relaxed text-brand-text/55">{field.helper}</p>
        )}
        <label className="flex min-h-[44px] items-start gap-3 text-sm text-brand-text/80">
          <input type="checkbox" data-testid={`field-${field.key}`} checked={!!value} onChange={(e) => onChange(field.key, e.target.checked)} className="mt-1 h-5 w-5 accent-brand-gold" />
          <span>{field.label}{field.required ? <span className="ml-1 text-brand-gold" aria-hidden="true">*</span> : ""}</span>
        </label>
      </div>
    );
  }
  return (<div>{label}{helper}<input id={id} type="text" placeholder={field.placeholder || ""} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className={common} /></div>);
}

export default function Profile() {
  const { user } = useAuth();
  const isAdminViewer = user?.role === "admin" || user?.role === "super_admin";
  const [profile, setProfile] = useState(null);
  const [activeN, setActiveN] = useState(1);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(0);
  // P0-4 — Structured save-error state so the UI can differentiate auth / validation /
  // network / unknown failures instead of silently swallowing them.
  //   kind:        "auth" | "validation" | "network" | "unknown"
  //   message:     user-facing string
  //   field:       optional — key of the specific field that failed validation
  //   retryable:   true → show a "Retry" affordance that calls save() again
  //   stickySaving: true → keep the "Saving…" state so the button doesn't falsely go idle
  const [saveError, setSaveError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Admin preview override (Task 2B). Defaults to most-complete mix.
  const [previewPlatforms, setPreviewPlatforms] = useState("onlyfans_plus_tiktok");

  useEffect(() => { document.title = "Profile · Omertàbella"; }, []);

  // Resolve which platform tags are active for the current view.
  // Creator: uses their own creator_profiles.platforms array.
  // Admin previewer: uses the dropdown override.
  const platformTags = useMemo(() => {
    let plats = [];
    if (isAdminViewer) plats = [previewPlatforms];
    else plats = profile?.platforms || [];
    if (!plats || plats.length === 0) return new Set(["onlyfans", "tiktok"]);
    const out = new Set();
    for (const p of plats) {
      if (p === "onlyfans_only") out.add("onlyfans");
      else if (p === "tiktok_only") out.add("tiktok");
      else if (p === "onlyfans_plus_tiktok") { out.add("onlyfans"); out.add("tiktok"); }
    }
    return out.size ? out : new Set(["onlyfans", "tiktok"]);
  }, [isAdminViewer, previewPlatforms, profile]);

  const isFieldVisible = useCallback((f) => {
    const req = f.platforms_required || [];
    if (req.length === 0) return true;
    return req.some((t) => platformTags.has(t));
  }, [platformTags]);

  const load = useCallback(async () => {
    const r = await apiClient.get("/me/profile");
    setProfile(r.data);
    const cur = (r.data.sections || {})[r.data.schema.find(s => s.n === activeN)?.key] || {};
    setDraft(cur);
  }, [activeN]);
  useEffect(() => { load(); }, [load]);

  const section = useMemo(() => profile?.schema?.find(s => s.n === activeN), [profile, activeN]);
  const visibleFields = useMemo(() => (section?.fields || []).filter(isFieldVisible), [section, isFieldVisible]);

  const onChange = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    if (!section) return;
    setSaving(true);
    // Clear only recoverable errors on new attempt — auth error stays until session refreshed.
    setSaveError((e) => (e?.kind === "auth" ? e : null));
    try {
      // Filter draft to only include keys that exist in the current section's
      // schema. Legacy Mongo data may have stale keys that the backend now
      // rejects with 400 (Bug A fix). Silently exclude them — they remain in
      // Mongo, just never re-saved through this section.
      const schemaKeys = new Set((section.fields || []).map((f) => f.key));
      const payload = {};
      for (const k of Object.keys(draft || {})) {
        if (schemaKeys.has(k)) payload[k] = draft[k];
      }
      const r = await apiClient.patch(`/me/profile/section/${section.n}`, payload);
      setProfile((p) => ({
        ...p,
        sections: { ...(p.sections || {}), [section.key]: r.data.data },
        completion: r.data.completion,
      }));
      setDraft(r.data.data);
      setSavedTick(Date.now());
      setLastSavedAt(new Date());
      setSaveError(null);
      setSaving(false);
    } catch (err) {
      // P0-4 — Structured error surfacing. Silent swallow (previous behavior) caused
      // creators to lose typed answers when their session expired mid-form.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      console.error("profile_patch_failed", { status, detail, section: section.n });

      if (status === 401 || status === 403) {
        // Auth error — do NOT flip "Saving…" back to idle. Keep the button locked
        // so the user knows something is wrong until they refresh / sign in again.
        setSaveError({
          kind: "auth",
          message: "Your session has expired — please sign in again to save your work.",
          retryable: false,
          stickySaving: true,
        });
        // NB: intentionally NOT calling setSaving(false) here. The button stays in
        // "Saving…" state as a persistent visual cue that saves aren't landing.
      } else if (status && status >= 400 && status < 500) {
        // Validation error — try to surface a field-level message when the backend
        // provides one, else fall back to a generic reason string.
        let field, message;
        if (detail && typeof detail === "object") {
          if (detail.error === "unknown_fields") {
            message = `Save failed: some field names weren't recognized (${(detail.fields || []).slice(0, 3).join(", ")}).`;
          } else if (Array.isArray(detail)) {
            // Pydantic list — pick first field
            const first = detail[0];
            field = first?.loc?.[first.loc.length - 1];
            message = `Save failed on "${field}": ${first?.msg || "invalid value"}`;
          } else {
            message = `Save failed: ${detail.reason || detail.message || JSON.stringify(detail).slice(0, 100)}`;
          }
        } else {
          message = `Save failed (HTTP ${status}). Please review your answers and try again.`;
        }
        setSaveError({ kind: "validation", message, field, retryable: false });
        setSaving(false);
      } else {
        // 5xx / network / unknown — retryable.
        setSaveError({
          kind: "network",
          message: status ? `Server error (HTTP ${status}) — couldn't save.` : "Network error — couldn't save your changes.",
          retryable: true,
        });
        setSaving(false);
      }
    }
  };
  const onBlurSave = () => save();
  // Format helper for the "Last saved" indicator.
  const fmtLastSaved = (d) => {
    if (!d) return null;
    const diff = Math.max(0, Date.now() - d.getTime());
    const s = Math.floor(diff / 1000);
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  };

  if (!profile) return <AppShell><div className="p-12 text-center text-brand-text/60">Loading the file…</div></AppShell>;
  const completion = profile.completion || { percent: 0, sections: [] };

  return (
    <AppShell>
      <div data-testid="profile-page" className="mx-auto max-w-6xl px-6 py-10">
        {isAdminViewer && (
          <div
            data-testid="profile-admin-preview-banner"
            className="mb-6 flex flex-col gap-3 rounded border border-brand-gold/40 bg-brand-gold/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-brand-gold">Admin preview</p>
              <p className="mt-1 text-sm text-brand-text/80">
                Previewing as <strong className="text-brand-gold">{({
                  onlyfans_only: "OnlyFans only",
                  tiktok_only: "TikTok only (live)",
                  onlyfans_plus_tiktok: "OnlyFans + TikTok Live",
                }[previewPlatforms])}</strong>. Fields & completion math adapt to the selected mix.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-text/60">View as</label>
              <select
                data-testid="profile-admin-preview-select"
                value={previewPlatforms}
                onChange={(e) => setPreviewPlatforms(e.target.value)}
                className="rounded border border-brand-gold/40 bg-[#141414] px-3 py-1.5 text-xs uppercase tracking-[0.18em] text-brand-text focus:border-brand-gold focus:outline-none"
              >
                <option value="onlyfans_plus_tiktok">OF + TikTok Live</option>
                <option value="onlyfans_only">OnlyFans only</option>
                <option value="tiktok_only">TikTok only</option>
              </select>
              <Link
                to="/admin"
                data-testid="profile-back-to-admin"
                className="shrink-0 rounded border border-brand-gold px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg"
              >
                ← Back
              </Link>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <Link to={isAdminViewer ? "/admin" : "/portal"} className="text-xs uppercase tracking-[0.24em] text-brand-text/50 hover:text-brand-gold">
            ← {isAdminViewer ? "Admin" : "Portal"}
          </Link>
          <span data-testid="profile-completion-percent" className="font-display text-xl text-brand-gold">{completion.percent}% complete</span>
        </div>
        <p className="mt-6 text-[10px] uppercase tracking-[0.32em] text-brand-gold">· The File ·</p>
        <h1 className="mt-2 font-display text-4xl text-brand-text sm:text-5xl">Your operating manual.</h1>
        <p className="mt-3 max-w-2xl text-sm text-brand-text/60">
          The deeper the file, the sharper the strategy. Every answer informs how we position, price, and protect you. Held in strict confidence.
        </p>

        {/* P0-4 — Persistent auth-error banner. Renders on 401/403 from save().
            Stays visible until the user reloads / re-authenticates, since Saving state
            is intentionally kept stuck to make the failure obvious. */}
        {saveError?.kind === "auth" && (
          <div
            data-testid="profile-save-auth-error"
            role="alert"
            className="mt-6 flex flex-col gap-3 rounded border border-brand-danger/60 bg-brand-danger/10 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-danger">Session expired</p>
              <p className="mt-1 text-sm text-brand-text">
                {saveError.message} Your recent changes have NOT been saved.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="profile-save-auth-reload"
                onClick={() => { window.location.reload(); }}
                className="rounded border border-brand-danger px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-danger hover:bg-brand-danger hover:text-brand-bg"
              >
                Sign in again
              </button>
            </div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          <aside data-testid="profile-section-nav" className="hidden lg:block">
            <ol className="space-y-1">
              {profile.schema.map((s) => {
                const st = completion.sections.find(x => x.n === s.n);
                const active = s.n === activeN;
                return (
                  <li key={s.n}>
                    <button
                      data-testid={`profile-nav-${s.n}`}
                      onClick={() => { setActiveN(s.n); }}
                      className={`group flex w-full items-center justify-between rounded px-3 py-2 text-left text-xs ${active ? "bg-brand-gold/10 text-brand-gold" : "text-brand-text/60 hover:bg-brand-text/5 hover:text-brand-text"}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`font-mono text-[10px] ${active ? "text-brand-gold" : "text-brand-text/40"}`}>{String(s.n).padStart(2, "0")}</span>
                        <span className="uppercase tracking-[0.12em]">{s.label}</span>
                      </span>
                      <span className={`text-[10px] ${st?.complete ? "text-emerald-400" : "text-brand-text/40"}`}>
                        {st?.percent ?? 0}%
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          <section className="rounded border border-brand-gold/20 bg-[#1A1A1A] p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-mono text-xs text-brand-text/40">{String(section.n).padStart(2,"0")} / {String(profile.schema.length).padStart(2,"0")}</span>
                <h2 className="mt-1 font-display text-2xl text-brand-text">{section.label}</h2>
                {section.recap && (
                  <p data-testid={`profile-section-recap-${section.n}`} className="mt-2 max-w-xl text-sm leading-relaxed text-brand-text/60">
                    {section.recap}
                  </p>
                )}
                {visibleFields.some((f) => f.required) && (
                  <p data-testid={`profile-section-legend-${section.n}`} className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/45">
                    <span className="text-brand-gold">*</span> required
                  </p>
                )}
                {/* P0-4 — "Last saved" indicator so the creator has visible proof their work is being persisted. */}
                {lastSavedAt && (
                  <p data-testid="profile-last-saved" className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-brand-text/45">
                    Last saved · {fmtLastSaved(lastSavedAt)}
                  </p>
                )}
              </div>
              <div className="text-right">
                <button
                  data-testid="profile-save-section"
                  onClick={save}
                  disabled={saving || saveError?.stickySaving}
                  className={`rounded border px-4 py-2 text-xs uppercase tracking-[0.24em] transition-colors disabled:opacity-50 ${
                    saveError && saveError.kind !== "auth"
                      ? "border-brand-danger text-brand-danger hover:bg-brand-danger hover:text-brand-bg"
                      : "border-brand-gold bg-transparent text-brand-gold hover:bg-brand-gold hover:text-brand-bg"
                  }`}
                >
                  {saving || saveError?.stickySaving ? "Saving…" : "Save section"}
                </button>
                {savedTick > 0 && !saving && !saveError && (<div data-testid="profile-saved-badge" className="mt-1 text-[10px] uppercase tracking-[0.24em] text-emerald-400">Saved</div>)}
                {/* P0-4 — Inline validation / network error under the save button. */}
                {saveError && saveError.kind !== "auth" && (
                  <div data-testid="profile-save-inline-error" className="mt-2 max-w-xs text-right">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-brand-danger">Save failed</p>
                    <p className="mt-0.5 text-[11px] text-brand-text/75">{saveError.message}</p>
                    {saveError.retryable && (
                      <button
                        type="button"
                        data-testid="profile-save-retry"
                        onClick={save}
                        className="mt-1 rounded border border-brand-danger px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-brand-danger hover:bg-brand-danger hover:text-brand-bg"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2" onBlur={onBlurSave}>
              {visibleFields.length === 0 && (
                <p data-testid="profile-section-empty" className="sm:col-span-2 rounded border border-brand-text/10 bg-[#141414] p-6 text-center text-sm text-brand-text/55">
                  Not applicable for your selected platforms.
                </p>
              )}
              {visibleFields.map((f) => (
                <div key={f.key} className={f.type === "longtext" || f.type === "ack" ? "sm:col-span-2" : ""}>
                  <Field field={f} value={draft[f.key]} onChange={onChange} />
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-between border-t border-brand-text/10 pt-4">
              <button data-testid="profile-prev" disabled={activeN === 1} onClick={() => setActiveN((n) => Math.max(1, n - 1))} className="text-xs uppercase tracking-[0.24em] text-brand-text/60 hover:text-brand-gold disabled:opacity-30">← Previous</button>
              <button data-testid="profile-next" disabled={!profile?.schema || activeN >= profile.schema.length} onClick={() => setActiveN((n) => Math.min((profile?.schema?.length || n), n + 1))} className="text-xs uppercase tracking-[0.24em] text-brand-text/60 hover:text-brand-gold disabled:opacity-30">Next →</button>
            </div>
          </section>
        </div>

        <UploadsPanel />
      </div>
    </AppShell>
  );
}
